import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SiFormItemComponent } from '@siemens/element-ng/form';
import {
    SelectItem,
    SelectOption,
    SiSelectComponent,
    SiSelectMultiValueDirective,
    SiSelectSimpleOptionsDirective
} from '@siemens/element-ng/select';

import { TranslateModule } from '@ngx-translate/core';
import { tap } from 'rxjs';
import { CloseOnScrollDirective } from '../../directives/close-on-scroll.directive';

@Component({
    selector: 'tran-select',
    imports: [
        ReactiveFormsModule,
        SiFormItemComponent,
        SiSelectComponent,
        SiSelectMultiValueDirective,
        SiSelectSimpleOptionsDirective,
        TranslateModule,
        CloseOnScrollDirective
    ],
    templateUrl: './select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectComponent<V = string> implements OnInit {
    public readonly control = input.required<FormControl<V | V[]>>();
    public readonly label = input.required<string>();
    public readonly placeholder = input<string>('');
    public readonly errorMessage = input<string>('');
    public readonly submitted = input(false);
    public readonly multi = input(false);
    public readonly readonly = input(false);
    public readonly required = input(false);
    public readonly options = input.required<SelectItem<V>[]>();
    public readonly hasSelectAll = input(false);
    public readonly selectAllLabel = input<string>('Select all');
    public readonly selectAllValue = input<V>('ALL' as V);

    private readonly destroyRef = inject(DestroyRef);

    protected readonly displayOptions = computed<SelectItem<V>[]>(() => {
        const baseOptions = this.options();

        if (!this.hasSelectAll() || !this.multi()) {
            return baseOptions;
        }

        const selectAllOption: SelectOption<V> = {
            type: 'option',
            value: this.selectAllValue(),
            label: this.selectAllLabel()
        };

        return [selectAllOption, ...baseOptions];
    });

    private readonly allValues = computed<V[]>(() => {
        return this.options()
            .filter((opt): opt is SelectOption<V> => opt.type === 'option')
            .map(opt => opt.value);
    });

    ngOnInit(): void {
        if (this.hasSelectAll() && this.multi()) {
            this.handleSelectAll();
        }
    }

    private handleSelectAll(): void {
        this.control()
            .valueChanges.pipe(
                tap(values => {
                    const newValues = (values ?? []) as V[];

                    if (!newValues.includes(this.selectAllValue())) {
                        return;
                    }

                    const allValuesList = this.allValues();
                    if (allValuesList.length === 0) {
                        return;
                    }

                    const selectedValues = newValues.filter(selectedValue => selectedValue !== this.selectAllValue());

                    const allSelected =
                        selectedValues.length === allValuesList.length &&
                        allValuesList.every(id => selectedValues.includes(id));

                    queueMicrotask(() => {
                        this.control().setValue(allSelected ? ([] as V[]) : allValuesList, { emitEvent: true });
                        this.control().markAsTouched();
                    });
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }
}
